import type {BasePanel} from '@Components/panel/panel.types';

export interface AiVoicePanelProps extends BasePanel {
  onAddAudioItem(itemId: string): Promise<void>;
  /** Adds the multi-select footer's picks in one batch (one add call, one undo step). Hosts without it get one onAddAudioItem call per pick. */
  onAddAudioItems?(itemIds: string[]): Promise<void>;
  /** Caps how many generated voices can be selected at once; omit for unlimited. Mirrors the AI Videos panel's host-driven cap (replace flows pass 1). */
  maxSelectionNumber?: number;
  onClose?(): void;
  showBackButton?: boolean;
  contentClassname?: string;
  isFullScreen?: boolean;
  enableDragToCanvas?: boolean;
}

export enum PromptSubfilterType {
  STANDARD,
  PLAYABLE,
}

export interface BaseSubfilter {
  id: number;
  name: string;
  type: PromptSubfilterType;
}

export interface PlayableSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.PLAYABLE;
  soundtrack: string;
  avatarUrl?: string;
  description: string;
  accent?: string;
  credits: number;
}

export interface StandardSubfilter extends BaseSubfilter {
  type: PromptSubfilterType.STANDARD;
}

export type PromptSubfilter = PlayableSubfilter | StandardSubfilter;

export enum AiVoicePanelErrorState {
  STANDARD,
  DAILY_LIMIT_REACHED,
}

export enum VoiceLanguage {
  ENGLISH = 'en',
  FRENCH = 'fr',
  SPANISH = 'es',
  GERMAN = 'de',
  ITALIAN = 'it',
  POLISH = 'pl',
  JAPANESE = 'ja',
  ARABIC = 'ar',
  TURKISH = 'tr',
  CANTONESE = 'yue',
  CATALAN = 'ca',
  CZECH = 'cs',
  DANISH = 'da',
  DUTCH = 'nl',
  FINNISH = 'fi',
  KOREAN = 'ko',
  MANDARIN = 'zh',
  NORWEGIAN = 'no',
  PORTUGUESE = 'pt',
  SWEDISH = 'sv',
}

export interface VoiceLanguageOption {
  language: VoiceLanguage;
  flagUrl: string;
  displayName: string;
}

export const DEFAULT_VOICE_LANGUAGE = VoiceLanguage.ENGLISH;
export const MAX_VOICE_PROMPT_LENGTH = 3000;

export enum AiVoicePanelDataKey {
  GENERATION_STATE = 'generationState',
  IS_AI_WRITER_OPEN = 'isAiWriterOpen',
}

export enum AiVoicePanelGenerationState {
  IDLE = 'IDLE',
  GENERATING = 'GENERATING',
  READY = 'READY',
  ERROR = 'ERROR',
}

export const LOADING_KEY_PREFIX = 'ai-voice-loading';
